import { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Inbox } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { EmailRow } from "./EmailRow";
import { GroupedEmails, groupEmails } from "./GroupedEmails";
import { MailSplitView } from "./MailSplitView";
import { useMailboxTopics } from "./useMailboxTopics";

const PAGE = 50;

const Empty = ({ text, testId }) => (
  <div className="py-14 flex flex-col items-center gap-2 text-center text-sm text-muted-foreground" data-testid={testId}>
    <Inbox className="w-8 h-8 text-slate-300" />{text}
  </div>
);

export function EmailList({ params, showTarget, emptyText, testId, onLoaded, view = "list", split = false }) {
  const topics = useMailboxTopics();
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const key = JSON.stringify(params);
  const grouped = view !== "list";

  const load = useCallback(async (append = false, offset = 0) => {
    const res = await mailboxApi.list({ ...JSON.parse(key), search: search.trim() || undefined, skip: offset, limit: grouped ? 500 : PAGE });
    setItems((prev) => (append ? [...(prev || []), ...res.data.items] : res.data.items));
    setTotal(res.data.total);
    onLoaded?.(res.data.total);
  }, [key, search, onLoaded, grouped]);

  useEffect(() => {
    const t = setTimeout(() => load().catch(() => setItems([])), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const searchBox = (
    <div className="space-y-1.5">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Betreff, Absender oder Text suchen…" className="pl-9 bg-background" data-testid={`${testId}-search`} />
      </div>
      {items?.length > 0 && <p className="text-xs text-muted-foreground px-0.5">{total} E-Mail{total === 1 ? "" : "s"}{grouped && total > items.length ? ` (die neuesten ${items.length} gruppiert)` : ""}</p>}
    </div>
  );
  const more = !grouped && items && items.length < total && (
    <div className="flex justify-center p-3"><Button variant="outline" size="sm" onClick={() => load(true, items.length)} data-testid={`${testId}-more`}>Weitere laden</Button></div>
  );
  const groups = grouped && items ? groupEmails(items, view, topics) : null;

  if (split) {
    return (
      <div data-testid={testId}>
        {items === null ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          : items.length === 0 && !search ? <div className="rounded-xl border bg-card"><Empty text={emptyText} testId={`${testId}-empty`} /></div>
            : <MailSplitView items={items} groups={groups} header={searchBox} onChanged={() => load()}
                footer={<>{items.length === 0 && <Empty text="Keine Treffer." testId={`${testId}-empty`} />}{more}</>} />}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="max-w-md">{searchBox}</div>
      {items === null ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Empty text={search ? "Keine Treffer." : emptyText} testId={`${testId}-empty`} />
      ) : (
        <>
          {grouped ? <GroupedEmails groups={groups} showTarget={showTarget} onChanged={() => load()} />
            : items.map((e) => <EmailRow key={e.id} email={e} showTarget={showTarget} onChanged={() => load()} />)}
          {more}
        </>
      )}
    </div>
  );
}

export function EntityEmailsTab({ targetType, targetId }) {
  return (
    <EmailList params={{ target_type: targetType, target_id: targetId }} testId="entity-emails"
      emptyText="Noch keine E-Mails aus dem Service-Postfach zugeordnet." />
  );
}
